import { defineStore, acceptHMRUpdate } from 'pinia'

export interface RedirectPages{
  UrlOud: string
}

export interface PageOverview{
  BrowserTitle: string,
  Code: string,
  CodeParent: string,
  MetaDescription: string,
  MetaKeywords: string,
  MetaTitle: string,
  NameMenu: string,
  NameUrl: string,
  PageType: string,
  RedirectPages?: RedirectPages[] | string,
  ShowHeadMenu: string,
  SubPage?: PageOverview[]
}

export const usePageOverviewStore = defineStore('pageOverview', {
  state: () => ({
    Code: '',
    PageOverview: [] as PageOverview[],
    initialized: false,
    loading: false,
    pageCodeUrlMapping: {} as Record<string, string> 
  }),

  getters: {
    // Zie ook functionsGlobal.js, lines 113-135
    // alleen nuttig voor pagina's waarbij type uniek is,
    // of om de eerste match van een bepaald pagina type te returnen
    getPageByType: (state) => (pageType: string) => {
      const findByPageType = (pages: PageOverview[]): PageOverview | undefined => {
        for(const page of pages){
          if (page.PageType === pageType) return page;
          if (page.SubPage && Array.isArray(page.SubPage)) {
            const found = findByPageType(page.SubPage as PageOverview[])
            if (found) return found          
          }
        }
        return undefined;
      }
      return findByPageType(state.PageOverview);
    },

    getPageUrlByPageCode: (state) => (code: string) => {
      return state.pageCodeUrlMapping[code] || ''
    },

    getPageByRoute: (state) => (route: string[]) => {
      let currentRoute = [...route]
      let counter = 0;
      const findByRoute = (pages: PageOverview[], route: string[]): PageOverview | undefined => {
        counter ++;
        for(const page of pages) {
          if(page.NameUrl === route[0] && route.length == 1) return page;
          if (page.SubPage && Array.isArray(page.SubPage) && page.NameUrl === route[0]) {
            route.shift()
            const foundSubPage = findByRoute(page.SubPage as PageOverview[], route)
            if (foundSubPage) return foundSubPage;
          }
        }
        return undefined;
      }
      return findByRoute(state.PageOverview, currentRoute);
    }
  },

  actions: {
    setInitialized(value: boolean) {
      this.initialized = value;
    },

    setLoading(value: boolean) {
      this.loading = value;
    },

    populate(pageOverviewData: any) {
      if (!pageOverviewData) {
        console.error('No pageOverviewData included')
        return
      }

      // pageOverviewData is een array
      if (Array.isArray(pageOverviewData)) {
        this.Code = ''
        this.PageOverview = pageOverviewData
        this.applyUrlMappings()
        this.setInitialized(true)
        return
      }

      // pageOverviewData is een Object met Code en PageOverview properties
      this.Code = pageOverviewData.Code
      this.PageOverview = Array.isArray(pageOverviewData.PageOverview) ? pageOverviewData.PageOverview : []
      this.applyUrlMappings()
      this.setInitialized(true)
    },

    applyUrlMappings() {
      const applyMapping = (pages: PageOverview[], parentPath: string = '') => {
        for (const page of pages) {
          const fullPath = parentPath ? `${parentPath}/${page.NameUrl}` : `/${page.NameUrl}`
          this.pageCodeUrlMapping[page.Code] = fullPath
          if (page.SubPage && Array.isArray(page.SubPage)) {
            applyMapping(page.SubPage as PageOverview[], fullPath)
          }
        }
      }
      applyMapping(this.PageOverview);
    },

    reset() {
      this.$reset()
      this.initialized = false
      this.loading = false
    }
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(usePageOverviewStore, import.meta.hot))
}